/**
 * Shared layout wrapper for onboarding step screens
 *
 * Provides consistent structure: icon, title, subtitle,
 * scrollable content area, and bottom action buttons.
 */

import type { ReactNode } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getTypographyStyle } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

type ActionButton = {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
};

type OnboardingStepLayoutProps = {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  primaryButton: ActionButton;
  secondaryButton?: ActionButton;
  onSkip?: () => void;
};

export function OnboardingStepLayout({
  icon,
  title,
  subtitle,
  children,
  primaryButton,
  secondaryButton,
  onSkip,
}: OnboardingStepLayoutProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  return (
    <View style={styles.container}>
      {/* Header section */}
      <View style={styles.header}>
        {icon && <View style={styles.iconContainer}>{icon}</View>}
        <Text style={[styles.title, { color: palette.text }]}>{title}</Text>
        {subtitle && (
          <Text style={[styles.subtitle, { color: palette.textMuted }]}>
            {subtitle}
          </Text>
        )}
      </View>

      {/* Content area */}
      <View style={styles.content}>{children}</View>

      {/* Bottom buttons */}
      <View style={styles.footer}>
        {secondaryButton && (
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: palette.border }]}
            onPress={secondaryButton.onPress}
            disabled={secondaryButton.disabled}
          >
            <Text style={[styles.secondaryButtonText, { color: palette.text }]}>
              {secondaryButton.label}
            </Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[
            styles.primaryButton,
            { backgroundColor: palette.primary },
            primaryButton.disabled && styles.buttonDisabled,
          ]}
          onPress={primaryButton.onPress}
          disabled={primaryButton.disabled}
        >
          <Text style={styles.primaryButtonText}>{primaryButton.label}</Text>
        </TouchableOpacity>
        {onSkip && (
          <TouchableOpacity style={styles.skipButton} onPress={onSkip}>
            <Text style={[styles.skipText, { color: palette.textMuted }]}>
              Skip for now
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const titleStyle = getTypographyStyle('title');
const subtitleStyle = getTypographyStyle('body');
const buttonTextStyle = getTypographyStyle('subheading');

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: Spacing.xl,
  },
  header: {
    alignItems: 'center',
    paddingTop: Spacing.xxl,
  },
  iconContainer: {
    marginBottom: Spacing.xxl,
  },
  title: {
    ...titleStyle,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: Spacing.sm,
  },
  subtitle: {
    ...subtitleStyle,
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: Spacing.xl,
    marginBottom: Spacing.lg,
  },
  content: {
    flex: 1,
  },
  footer: {
    paddingVertical: Spacing.lg,
    gap: Spacing.sm,
  },
  primaryButton: {
    paddingVertical: Spacing.md + 2,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  primaryButtonText: {
    ...buttonTextStyle,
    color: '#FFFFFF',
  },
  secondaryButton: {
    paddingVertical: Spacing.md + 2,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
    borderWidth: 1,
  },
  secondaryButtonText: {
    ...buttonTextStyle,
  },
  skipButton: {
    alignItems: 'center',
    paddingVertical: Spacing.sm,
  },
  skipText: {
    ...subtitleStyle,
  },
});
